import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { CalendarBlank as DateIcon } from "@phosphor-icons/react/CalendarBlank";
import { CaretDown as CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { Clock as ClockIcon } from "@phosphor-icons/react/Clock";
import { Copy as CopyIcon } from "@phosphor-icons/react/Copy";
import { CornersOut as CornersOutIcon } from "@phosphor-icons/react/CornersOut";
import { DotsThree as DotsThreeIcon } from "@phosphor-icons/react/DotsThree";
import { DownloadSimple as DownloadIcon } from "@phosphor-icons/react/DownloadSimple";
import { Intersect as IntersectIcon } from "@phosphor-icons/react/Intersect";
import { Printer as PrintIcon } from "@phosphor-icons/react/Printer";
import { Table as TableIcon } from "@phosphor-icons/react/Table";
import { X as XIcon } from "@phosphor-icons/react/X";

import { ToolPanel } from "../../../tool-panel";
import { usePopover } from "../hooks/use-popover";
import type { QueryField, QueryResults as QueryResultsType } from "../types";
import type { DownloadFormat } from "./download-menu";
import { QueryReport } from "./query-report";
import { ResultsTable } from "./results-table";

export interface QueryResultsProps {
	/** Query results data */
	results: QueryResultsType;
	/** Query fields for table and report display */
	queryFields?: QueryField[];
	/** Panel title */
	title?: string;
	/** Callback when close button is clicked */
	onClose?: () => void;
	/** Callback when new query button is clicked */
	onNewQuery?: () => void;
	/** Callback when download is requested */
	onDownload?: (format: DownloadFormat) => void;
	/** Loading state for downloads */
	downloadLoading?: boolean;
	/** Loading state for initial data load */
	loading?: boolean;
	allowedDownloadFormats?: DownloadFormat[];
	page?: number;
	totalPages?: number;
	onPageChange?: (page: number) => void;
	/** Callback when a row is clicked for feature highlighting */
	onRowClick?: (row: any) => void;
	/** Array of column names to exclude from results table */
	excludeColumns?: string[];
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
	return (
		<Box
			role="tabpanel"
			hidden={value !== index}
			id={`results-tabpanel-${index}`}
			aria-labelledby={`results-tab-${index}`}
			sx={{ height: "100%", overflow: "hidden" }}>
			{value === index && children}
		</Box>
	);
}

// Download format configuration (moved outside component for performance)
const downloadFormats = [
	{
		id: "csv" as DownloadFormat,
		label: "CSV",
	},
	{
		id: "shp" as DownloadFormat,
		label: "Shapefile",
	},
	{
		id: "kml" as DownloadFormat,
		label: "KML",
	},
];

export function QueryResults({
	results,
	queryFields = [],
	title = "Query Results",
	onClose,
	onNewQuery,
	onDownload,
	downloadLoading = false,
	loading = false,
	allowedDownloadFormats = [],
	page,
	totalPages,
	onPageChange,
	onRowClick,
	excludeColumns = [],
}: QueryResultsProps): React.JSX.Element {
	const [activeTab, setActiveTab] = React.useState(0);
	const [fullscreenOpen, setFullscreenOpen] = React.useState(false);
	const [selectedRowId, setSelectedRowId] = React.useState<
		number | string | undefined
	>();
	const popover = usePopover<HTMLDivElement>();
	const reportActionsPopover = usePopover<HTMLButtonElement>();

	const formatDate = (timestamp: string) => {
		return new Date(timestamp).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatTime = (timestamp: string) => {
		return new Date(timestamp).toLocaleTimeString("en-US", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatExecutionTime = (ms: number) => {
		if (ms < 1000) {
			return `${ms.toFixed(0)}ms`;
		}
		return `${(ms / 1000).toFixed(1)}s`;
	};

	// Dynamically build tabs array with Report first, Table second
	const tabs = React.useMemo(() => {
		const tabList: { id: string; label: string }[] = [];

		if (results.report?.enabled) {
			tabList.push({
				id: "report",
				label: "Report",
			});
		}

		tabList.push({
			id: "table",
			label: "Results Table",
		});

		return tabList;
	}, [results.report?.enabled]);

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		setActiveTab(newValue);
	};

	const handleDownload = (format: DownloadFormat) => {
		onDownload?.(format);
		popover.handleClose();
	};

	const handleRowClick = (row: any) => {
		const rowId = row.gid || row.id;
		if (selectedRowId === rowId) {
			setSelectedRowId(undefined);
			onRowClick?.(null);
		} else {
			setSelectedRowId(rowId);
			onRowClick?.(row);
		}
	};

	return (
		<ToolPanel
			title={title}
			onBack={onNewQuery}
			onClose={onClose}
			actions={
				allowedDownloadFormats.length ? (
					<ButtonGroup
						ref={popover.anchorRef}
						variant="outlined"
						size="small">
						<Button
							startIcon={<DownloadIcon size={16} />}
							onClick={() => handleDownload(allowedDownloadFormats[0]!)}
							disabled={downloadLoading}>
							Download results
						</Button>
						<Button
							onClick={popover.handleToggle}
							size="small"
							disabled={downloadLoading}>
							<CaretDownIcon size={16} />
						</Button>
					</ButtonGroup>
				) : null
			}>
			{/* Results report and table */}
			<Stack
				spacing={1}
				sx={{ height: "100%" }}>
				{/* Meta information */}
				<Stack
					direction="row"
					spacing={1}
					sx={{ flexWrap: "wrap", gap: 1, pb: 1 }}>
					<Tooltip title={`Source query: ${results.queryConfig.displayName}`}>
						<Chip
							icon={<IntersectIcon size={16} />}
							label={results.queryConfig.displayName}
							variant="outlined"
							size="small"
						/>
					</Tooltip>
					<Tooltip
						title={`Query returned ${results.resultCount.toLocaleString()} results`}>
						<Chip
							icon={<TableIcon size={16} />}
							label={`${results.resultCount.toLocaleString()} results`}
							variant="outlined"
							size="small"
						/>
					</Tooltip>
					<Tooltip
						title={`Query execution time: ${formatExecutionTime(results.metadata.executionTime)}`}>
						<Chip
							icon={<ClockIcon size={16} />}
							label={formatExecutionTime(results.metadata.executionTime)}
							variant="outlined"
							size="small"
						/>
					</Tooltip>
					<Tooltip
						title={`Query executed on ${formatDate(results.metadata.timestamp)}`}>
						<Chip
							icon={<DateIcon size={16} />}
							label={formatTime(results.metadata.timestamp)}
							variant="outlined"
							size="small"
						/>
					</Tooltip>
				</Stack>

				{/* Tabs (if more than one available) */}
				{tabs.length > 1 && (
					<Box
						sx={{
							borderBottom: 1,
							borderColor: "divider",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}>
						<Tabs
							value={activeTab}
							onChange={handleTabChange}>
							{tabs.map((tab, index) => (
								<Tab
									key={tab.id}
									label={tab.label}
									id={`results-tab-${index}`}
									aria-controls={`results-tabpanel-${index}`}
									sx={{
										minHeight: 32,
										textTransform: "none",
										fontSize: "0.875rem",
									}}
								/>
							))}
						</Tabs>

						{/* Report actions menu - only show when Report tab is active */}
						{tabs[activeTab]?.id === "report" && (
							<Tooltip title="Report actions">
								<IconButton
									ref={reportActionsPopover.anchorRef}
									onClick={reportActionsPopover.handleToggle}
									size="small">
									<DotsThreeIcon size={16} />
								</IconButton>
							</Tooltip>
						)}

						{/* Fullscreen button - only show when Results Table tab is active */}
						{tabs[activeTab]?.id !== "report" && (
							<Tooltip title="View table in fullscreen">
								<IconButton
									onClick={() => setFullscreenOpen(true)}
									size="small">
									<CornersOutIcon size={16} />
								</IconButton>
							</Tooltip>
						)}
					</Box>
				)}

				{/* Tab Content */}
				<Box sx={{ flex: 1, overflow: "hidden" }}>
					{tabs.map((tab, index) => (
						<TabPanel
							key={tab.id}
							value={activeTab}
							index={index}>
							{tab.id === "report" ? (
								<Box sx={{ height: "100%", overflow: "auto", p: 1 }}>
									<QueryReport
										results={results}
										queryFields={queryFields}
									/>
								</Box>
							) : (
								<ResultsTable
									results={results}
									queryFields={queryFields}
									loading={loading}
									page={page}
									totalPages={totalPages}
									onPageChange={onPageChange}
									onRowClick={handleRowClick}
									selectedRowId={selectedRowId}
									excludeColumns={excludeColumns}
								/>
							)}
						</TabPanel>
					))}
				</Box>
			</Stack>

			{/* Download Format Menu */}
			<Menu
				anchorEl={popover.anchorRef.current}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				onClose={popover.handleClose}
				open={popover.open}>
				{downloadFormats
					.filter((format) => allowedDownloadFormats.includes(format.id))
					.map((format) => {
						return (
							<MenuItem
								key={format.id}
								onClick={() => handleDownload(format.id)}
								disabled={downloadLoading}>
								<ListItemText primary={format.label} />
							</MenuItem>
						);
					})}
			</Menu>

			{/* Report Actions Menu */}
			<Menu
				anchorEl={reportActionsPopover.anchorRef.current}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				onClose={reportActionsPopover.handleClose}
				open={reportActionsPopover.open}>
				<MenuItem
					onClick={() => {
						// Placeholder for copy to clipboard functionality
						alert("Coming soon!");
						reportActionsPopover.handleClose();
					}}>
					<ListItemIcon>
						<CopyIcon size={20} />
					</ListItemIcon>
					<ListItemText primary="Copy to Clipboard" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						// Placeholder for export as PDF functionality
						alert("Coming soon!");
						reportActionsPopover.handleClose();
					}}>
					<ListItemIcon>
						<DownloadIcon size={20} />
					</ListItemIcon>
					<ListItemText primary="Export as PDF" />
				</MenuItem>
				<MenuItem
					onClick={() => {
						// Placeholder for print functionality
						window.print();
						reportActionsPopover.handleClose();
					}}>
					<ListItemIcon>
						<PrintIcon size={20} />
					</ListItemIcon>
					<ListItemText primary="Print" />
				</MenuItem>
			</Menu>

			{/* Fullscreen Table Dialog */}
			<Dialog
				open={fullscreenOpen}
				onClose={() => setFullscreenOpen(false)}
				maxWidth={false}
				fullWidth
				PaperProps={{
					sx: {
						height: "90vh",
						maxHeight: "90vh",
						m: 2,
					},
				}}>
				<Toolbar
					sx={{
						justifyContent: "space-between",
						borderBottom: 1,
						borderColor: "divider",
					}}>
					<Typography
						variant="h6"
						component="div">
						{title} - Results Table
					</Typography>
					<IconButton
						onClick={() => setFullscreenOpen(false)}
						size="small">
						<XIcon size={20} />
					</IconButton>
				</Toolbar>
				<Box sx={{ flex: 1, overflow: "hidden" }}>
					<ResultsTable
						results={results}
						queryFields={queryFields}
						loading={loading}
						page={page}
						totalPages={totalPages}
						onPageChange={onPageChange}
						onRowClick={handleRowClick}
						selectedRowId={selectedRowId}
						excludeColumns={excludeColumns}
					/>
				</Box>
			</Dialog>
		</ToolPanel>
	);
}
